import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { css, styled } from "goober";
import { Button } from "clutch/src/Button/Button.js";
import { mobile } from "clutch/src/Style/style.mjs";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP } from "@/__main__/constants.mjs";
import { writeSettings } from "@/app/actions.mjs";
import appEventBus from "@/app/app-event-bus.mjs";
import type { MenuListItemProps } from "@/app/AppShellMenuListItem.jsx";
import MenuListItem from "@/app/AppShellMenuListItem.jsx";
import { appShellUserDropdownRefs } from "@/app/AppShellUserDropdown.refs.jsx";
import {
  ACTIVE_GAMES,
  GAME_SHORT_NAMES,
  hardCodeURLs,
  HUB_NAME_MAP,
  HUB_SYMBOL_SETTINGS,
  LOCALES,
} from "@/app/constants.mjs";
import { FEEDBACK, HELP, PRIVACY_POLICY } from "@/app/constants-nav.mjs";
import appRefs from "@/app/refs.mjs";
import CheckIcon from "@/inline-assets/check.svg";
import DiscordIcon from "@/inline-assets/discord-logo.svg";
import AvatarIcon from "@/inline-assets/nav-guest-profile.svg";
import RedditIcon from "@/inline-assets/social-reddit.svg";
import XIcon from "@/inline-assets/social-x.svg";
import { DialogContent, DialogHeader, DialogWindow } from "@/shared/Dialog.jsx";
import FullscreenOverlay from "@/shared/FullscreenOverlay.jsx";
import {
  createModalNavigation,
  useModalNavigation,
  useModalRouter,
} from "@/shared/ModalNavigation.jsx";
import { classNames } from "@/util/class-names.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import { EVENT_DROPDOWN_TOGGLE } from "@/util/use-dropdown-ref.mjs";
import { sendInteractionEvent } from "@/util/use-interaction-event.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const DropdownWrapper = styled("div")`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-0_5);
  top: 100%;
  /* fit dropdown between header ad & right rail ads */
  left: 55%;
  translate: -50% 0;
  min-width: calc(var(--sp-1) * 40);
  padding: var(--sp-1);
  border-radius: var(--sp-2);
  border: 1px solid var(--shade3-15);
  background: var(--shade7);
  box-shadow:
    0px 12px 24px 0px rgba(7, 8, 12, 0.25),
    0px 4px 12px 0px rgba(7, 8, 12, 0.25);
  z-index: 1;

  /* this is to prevent misclicks near an ad, if not needed this is safe to remove */
  &::before {
    position: absolute;
    z-index: -1;
    display: block;
    content: " ";
    inset: -5px -30px 15px -30px;
  }

  li {
    a,
    button {
      border-radius: var(--br);
    }

    svg {
      width: var(--sp-3_5);
      height: var(--sp-3_5);
    }
  }

  hr {
    width: 100%;
    height: var(--sp-px);

    background: var(--shade3-15);
    border: none;
  }

  ${mobile} {
    display: none;
  }

  .social-links {
    display: flex;
    gap: 0.5rem;
    width: 100%;
    padding-inline: 0.5rem;
    padding-block-end: 0.25rem;

    a {
      flex: 1;
      display: grid;
      place-content: center;
      padding: 0.25rem;
      color: var(--color);
      background: color-mix(in hsl, var(--color) 15%, transparent);
      border-radius: var(--br);
      outline-offset: 2px;

      &:hover,
      &:focus {
        outline: 2px solid var(--shade5);
      }
    }

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

export const UserDropdownWrapper = styled("div", React.forwardRef)`
  --user-color: var(--shade1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-1_5);
  color: var(--user-color);
  background-color: color-mix(in srgb, var(--user-color) 7%, transparent);
  border-radius: 10rem;
  padding-inline-end: 0.75rem;
  cursor: pointer;

  @container app (width <= 767px) {
    padding-inline: var(--sp-2);
    background-color: transparent;
  }

  .button {
    background-color: color-mix(in srgb, var(--user-color) 10%, transparent);

    svg {
      color: var(--user-color);
    }
  }

  .dropdown {
    display: none;
  }

  .nav-item--profile-icon {
  }

  &:hover {
    &:not(.inert) .dropdown {
      display: flex;
    }
  }
`;

const ProfileIconContainer = () => css`
  & {
    position: relative;
    display: flex;
    z-index: 2;
    cursor: pointer;
  }
`;

const COMMON_MENU_ITEMS: MenuListItemProps[] = [HELP, PRIVACY_POLICY];

const SETTINGS: MenuListItemProps = {
  text: ["common:navigation.settings", "Settings"],
  href: `/${HUB_NAME_MAP[HUB_SYMBOL_SETTINGS]}`,
};

const useSettings = () => {
  const gameSymbol = useGameSymbol();

  const showGameSettings = gameSymbol && ACTIVE_GAMES.includes(gameSymbol);
  return {
    text: SETTINGS.text,
    href: `${SETTINGS.href}${
      showGameSettings ? `/${GAME_SHORT_NAMES[gameSymbol]}` : ""
    }`,
  };
};

export const UserNameWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  padding: var(--sp-2) var(--sp-2) var(--sp-1);
  justify-content: center;

  .username {
    color: var(--shade0);
    white-space: nowrap;
  }
`;

const UserName = ({ name }) => {
  const { UserNameWrapper: UserNameWrapperElement = UserNameWrapper } =
    useSnapshot(appShellUserDropdownRefs);

  return (
    <UserNameWrapperElement>
      <span className="type-callout--semi username">{name}</span>
    </UserNameWrapperElement>
  );
};

const DropdownMenu = ({ onClick }) => {
  const { user } = useSnapshot(readState);

  const { t } = useTranslation();

  return (
    <DropdownWrapper className="dropdown">
      {user ? (
        <UserName name={user.name} />
      ) : (
        <Button
          emphasis="high"
          size="small"
          href="/account"
          block
          onClick={() => {
            onClick();
            sendInteractionEvent("app-navigation-dropdown-sign-up-button");
          }}
        >
          {t("common:account.signUp", "Sign Up")}
        </Button>
      )}

      <ul>
        <NavItems
          itemClassName="type-callout"
          closeModal={onClick}
          showGroups
        />
      </ul>
      <hr />
      <div className="social-links">
        <a
          href={hardCodeURLs.BLITZ_DISCORD_VANITY}
          target="_blank"
          rel="noreferrer"
          style={{ "--color": "#5865f2" }}
          onClick={() => {
            sendInteractionEvent("app-navigation-social", {
              platform: "discord",
            });
          }}
        >
          <DiscordIcon />
        </a>
        <a
          href={hardCodeURLs.BLITZ_TWITTER}
          target="_blank"
          rel="noreferrer"
          style={{ "--color": "white" }}
          onClick={() => {
            sendInteractionEvent("app-navigation-social", { platform: "x" });
          }}
        >
          <XIcon />
        </a>
        <a
          href={hardCodeURLs.BLITZ_REDDIT}
          target="_blank"
          rel="noreferrer"
          style={{ "--color": "#FF5700" }}
          onClick={() => {
            sendInteractionEvent("app-navigation-social", {
              platform: "reddit",
            });
          }}
        >
          <RedditIcon />
        </a>
      </div>
    </DropdownWrapper>
  );
};

type MobileDropdownMenuProps = {
  isOpen?: boolean;
  closeModal: () => void;
};

const MobileNavigation = createModalNavigation<{
  home: void;
  selectLanguage: void;
}>("app-user-dropdown");

const MobileDropdownMenu = ({ closeModal }: MobileDropdownMenuProps) => {
  const modalRouter = useModalNavigation(MobileNavigation, "home");

  return (
    <DialogWindow width={550}>
      <MobileNavigation.Navigator
        modalRouter={modalRouter}
        onPopScope={closeModal}
      >
        <MobileNavigation.Route name="home">
          <ModalRouteHomeView closeModal={closeModal} />
        </MobileNavigation.Route>

        <MobileNavigation.Route name="selectLanguage">
          <ModalRouteLanguageView />
        </MobileNavigation.Route>
      </MobileNavigation.Navigator>
    </DialogWindow>
  );
};

function ModalRouteHomeView({ closeModal }: { closeModal: () => void }) {
  const modalRouter = useModalRouter(MobileNavigation);

  const { user } = useSnapshot(readState);

  const onSelectLanguage = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.stopPropagation();

      modalRouter.pushRoute("selectLanguage");
    },
    [modalRouter],
  );

  return (
    <DialogContent>
      <DialogHeader
        Icon={
          <div
            {...classNames(ProfileIconContainer(), "button")}
            className="button"
          >
            <AvatarIcon />
          </div>
        }
        title={user?.name ?? "Guest"}
      />

      <NavMenuList>
        <NavItems
          itemClassName="type-subtitle--semi"
          closeModal={closeModal}
          onSelectLanguage={onSelectLanguage}
        />
      </NavMenuList>
    </DialogContent>
  );
}

function ModalRouteLanguageView() {
  const modalRouter = useModalRouter(MobileNavigation);

  const popRoute = useCallback(() => modalRouter.popRoute(), [modalRouter]);

  const onChangeSelectedLanguage = useCallback(
    (code) => writeSettings(["selectedLanguage"], code),
    [],
  );

  const {
    i18n: { language: languageCode },
  } = useTranslation();

  return (
    <DialogContent>
      <DialogHeader
        title={["common:language", "Language"]}
        onPressBack={popRoute}
      />

      <NavMenuList>
        {LOCALES.map(([code, name]) => (
          <MenuListItem
            key={code}
            text={name}
            Icon={languageCode === code && <CheckIcon />}
            className="type-subtitle--semi"
            onClick={() => {
              onChangeSelectedLanguage(code);
              sendInteractionEvent("app-navigation-change-language-button");
            }}
          />
        ))}
      </NavMenuList>
    </DialogContent>
  );
}

const NavMenuList = styled("ul")`
  li {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-2);

    color: var(--shade0);

    border-top: 1px solid var(--shade3-15);

    overflow: hidden;

    .link {
      padding: var(--sp-3) var(--sp-6);

      border-radius: 0;
    }

    svg {
      width: var(--sp-5_5);
      height: var(--sp-5_5);
    }
  }
`;

function NavItems({
  itemClassName,
  closeModal,
  onSelectLanguage,
  showGroups,
}: {
  itemClassName?: string;
  closeModal: () => void;
  onSelectLanguage?: React.MouseEventHandler<HTMLAnchorElement>;
  showGroups?: boolean;
}) {
  const accountMenuItems = useSnapshot(appRefs.accountMenuItems);

  const {
    i18n: { language: languageCode },
  } = useTranslation();

  const currentLocale = useMemo(() => {
    return LOCALES.find((l) => l[0] === languageCode)?.[1] ?? languageCode;
  }, [languageCode]);

  const settings = useSettings();

  const navItems: (
    | MenuListItemProps
    | React.FC<{
        className?: string;
        onClick: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
      }>
  )[][] = useMemo(() => {
    return [
      [
        accountMenuItems.slotOne,
        settings,
        {
          text: ["common:discord", "Discord"],
          href: hardCodeURLs.BLITZ_DISCORD_VANITY,
          onClick: () => {
            sendInteractionEvent("app-navigation-discord-button");
          },
          className: css`
            background-color: purple;
            color: white;
          `,
          icon: <DiscordIcon />,
        },
        accountMenuItems.slotTwo,
        {
          text: [
            "common:languageSelected",
            `Language: {{name}}`,
            {
              name: currentLocale,
            },
          ],
          href: !onSelectLanguage
            ? `/${HUB_NAME_MAP[HUB_SYMBOL_SETTINGS]}`
            : undefined,
          onClick: onSelectLanguage,
        },
      ],
      [IS_APP ? FEEDBACK : null, ...COMMON_MENU_ITEMS],
      [accountMenuItems.footerSlot],
    ];
  }, [
    accountMenuItems.footerSlot,
    accountMenuItems.slotOne,
    accountMenuItems.slotTwo,
    currentLocale,
    onSelectLanguage,
    settings,
  ]);

  return (
    <>
      {navItems.map((navGroup, i) => {
        return (
          <React.Fragment key={i}>
            {showGroups && <hr />}

            {navGroup.map((menuItem, j) => {
              if (!menuItem) return null;

              if (menuItem instanceof Function) {
                return React.createElement(menuItem, {
                  key: j,
                  onClick: closeModal,
                  ...classNames(itemClassName),
                });
              }

              return (
                <MenuListItem
                  key={j}
                  {...menuItem}
                  onClick={(event) => {
                    menuItem.onClick?.(event);

                    if (!event.isPropagationStopped()) {
                      closeModal?.();
                    }
                  }}
                  {...classNames(itemClassName)}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}

export function UserDropdown() {
  const { t } = useTranslation();
  const {
    UserDropdownWrapper: UserDropdownWrapperElement = UserDropdownWrapper,
  } = useSnapshot(appShellUserDropdownRefs);
  const { user } = useSnapshot(readState);

  const [isMenuAvailable, setIsMenuAvailable] = useState(true);

  const onMenuOpened = useCallback(() => {
    setIsMenuAvailable(true);

    appEventBus.emit(EVENT_DROPDOWN_TOGGLE);
  }, []);

  const makeMenuAvailable = useCallback(() => {
    setIsMenuAvailable(true);
  }, []);

  const makeMenuUnavailable = useCallback(() => {
    setIsMenuAvailable(false);
  }, []);

  const link = user ? "/settings" : "/account";

  return (
    <UserDropdownWrapperElement
      onMouseLeave={makeMenuAvailable}
      onMouseEnter={onMenuOpened}
      {...classNames("user-dropdown", !isMenuAvailable && "inert")}
    >
      <a href={link} {...classNames(ProfileIconContainer(), "button")}>
        <AvatarIcon className="nav-item--profile-icon" />
      </a>
      <a href={link} className="username type-caption--semi">
        {user
          ? (user?.name ?? t("common:guest", "Guest"))
          : t("common:loginSignup", "Login / Signup")}
      </a>
      <DropdownMenu onClick={makeMenuUnavailable} />
    </UserDropdownWrapperElement>
  );
}

export function MobileUserDropdown() {
  const {
    UserDropdownWrapper: UserDropdownWrapperElement = UserDropdownWrapper,
  } = useSnapshot(appShellUserDropdownRefs);

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <UserDropdownWrapperElement className="mobile-menu-item">
      <button
        onClick={openModal}
        className="button"
        style={{ borderRadius: "var(--br)", paddingBlock: "var(--sp-1)" }}
      >
        <div className={ProfileIconContainer()}>
          <AvatarIcon className="nav-item--profile-icon" />
        </div>
      </button>

      <FullscreenOverlay isOpen={isModalOpen} onModalClose={closeModal}>
        <MobileDropdownMenu closeModal={closeModal} />
      </FullscreenOverlay>
    </UserDropdownWrapperElement>
  );
}
